import type { MaybeRefOrGetter } from 'vue';

export type ShortsOnboardingDismissReason =
	| 'cta'
	| 'overlay_click'
	| 'scroll'
	| 'keyboard_escape'
	| 'keyboard_arrow';

export type ShortsOnboardingStatus = 'shown' | 'not_shown';

interface UseShortsFeedOnboardingOptions {
	enabled: MaybeRefOrGetter<boolean>;
	onDismiss?: (reason: ShortsOnboardingDismissReason) => void;
}

export const SHORTS_FEED_ONBOARDING_COOKIE = 'shorts-feed-onboarding';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export function useShortsFeedOnboarding(
	options: UseShortsFeedOnboardingOptions
) {
	const cookie = useCookie<ShortsOnboardingStatus>(
		SHORTS_FEED_ONBOARDING_COOKIE,
		{
			maxAge: ONE_YEAR_SECONDS,
			sameSite: 'lax',
			default: () => 'not_shown'
		}
	);

	// `isHydrating` is true only on the first client render after SSR; false on
	// subsequent SPA navigations. Captured at setup to gate the SSR entry case.
	const isHydratedEntry = useNuxtApp().isHydrating;

	const isVisible = ref(false);

	function show() {
		if (!import.meta.client) return;
		if (isHydratedEntry) return;
		if (!toValue(options.enabled)) return;
		if (cookie.value === 'shown') return;
		isVisible.value = true;
	}

	function dismiss(reason: ShortsOnboardingDismissReason) {
		if (!isVisible.value) return;
		cookie.value = 'shown';
		isVisible.value = false;
		options.onDismiss?.(reason);
	}

	return {
		isVisible: readonly(isVisible),
		show,
		dismiss
	};
}
